import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Divider,
} from "@mui/material";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import {
  getPendingDuplicateCandidates,
  resolveDuplicateCandidate,
} from "../store/modules/common/commonActions";
import { DuplicateResolutionType } from "../store/repsaApi";
import { DuplicateCandidateModel } from "../types/types";

import { RootState, AppDispatch } from "../store/store";

const DuplicateCandidateAlert = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resolutionAction, setResolutionAction] = useState<{
    type: DuplicateResolutionType;
    keepSource?: boolean;
  } | null>(null);
  const [selectedEntityToKeep, setSelectedEntityToKeep] = useState<
    "source" | "duplicate" | null
  >(null);

  const fullState = useSelector((state: any) => state);

  const duplicateCandidatesState = useSelector(
    (state: RootState) =>
      state.commonData?.duplicateCandidates || {
        data: null as DuplicateCandidateModel[] | null,
        loading: false,
        error: null,
        resolving: false,
        resolvingId: null,
        resolveError: null,
      }
  );

  const {
    data: duplicateCandidates,
    loading,
    error,
    resolving,
    resolvingId,
  } = duplicateCandidatesState;

  useQuery({
    queryKey: ["getPendingDuplicateCandidates"],
    queryFn: () => {
      dispatch(getPendingDuplicateCandidates());
      return Promise.resolve(null);
    },
    refetchOnWindowFocus: false,
  });

  if (!fullState.commonData || !fullState.commonData.duplicateCandidates) {
    return null;
  }

  const handleNext = () => {
    if (duplicateCandidates && currentIndex < duplicateCandidates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const openConfirmDialog = (
    type: DuplicateResolutionType,
    keepSource?: boolean
  ) => {
    setResolutionAction({ type, keepSource });
    // Reset the entity selection when opening the dialog
    setSelectedEntityToKeep(null);
    setConfirmDialogOpen(true);
  };

  const handleConfirmResolve = async () => {
    if (duplicateCandidates && resolutionAction) {
      const candidate = duplicateCandidates[
        currentIndex
      ] as DuplicateCandidateModel;

      // For merge action, ensure an entity has been selected
      if (
        resolutionAction.type === DuplicateResolutionType.Merge &&
        selectedEntityToKeep === null
      ) {
        return; // Don't proceed if no entity selected for merge
      }

      // Determine whether to keep the source entity based on selection
      const keepSource =
        resolutionAction.type === DuplicateResolutionType.Merge
          ? selectedEntityToKeep === "source" // Use the selected entity
          : resolutionAction.keepSource; // Use the default for ignore action

      // If merging, determine which entity name will be deleted (merged into the other)
      const mergedEntityName =
        resolutionAction.type === DuplicateResolutionType.Merge
          ? keepSource
            ? candidate.duplicateEntityName
            : candidate.sourceEntityName
          : null;
      const entityType = candidate.sourceEntityType;

      try {
        await dispatch(
          resolveDuplicateCandidate(
            candidate.duplicateCandidateId,
            resolutionAction.type,
            keepSource
          )
        );

        // If this was a merge, remove all other duplicate candidates that match
        // the merged entity name and type
        if (
          resolutionAction.type === DuplicateResolutionType.Merge &&
          mergedEntityName &&
          entityType
        ) {
          // Find candidates to remove (those with the same entity type and containing the merged entity name)
          const candidatesToRemove = duplicateCandidates.filter(
            (dc, index) =>
              index !== currentIndex && // Don't include the current candidate
              dc.sourceEntityType === entityType &&
              (dc.sourceEntityName === mergedEntityName ||
                dc.duplicateEntityName === mergedEntityName)
          );

          // Remove these candidates by resolving them as "Ignore" to clean them up
          for (const candidateToRemove of candidatesToRemove) {
            await dispatch(
              resolveDuplicateCandidate(
                candidateToRemove.duplicateCandidateId,
                DuplicateResolutionType.Ignore,
                true // Keep source doesn't matter for ignore
              )
            );
          }
        }

        setConfirmDialogOpen(false);

        // Refresh the duplicate candidates list to get updated data
        dispatch(getPendingDuplicateCandidates());

        // Reset current index to 0 since the list may have changed significantly
        setCurrentIndex(0);
      } catch (error) {
        console.error("Error resolving duplicate candidate:", error);
        // Handle error appropriately
      }
    }
  };

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  if (!duplicateCandidates || duplicateCandidates.length === 0) {
    return null;
  }

  const currentCandidate = duplicateCandidates[
    currentIndex
  ] as DuplicateCandidateModel;
  const isResolving =
    resolving && resolvingId === currentCandidate.duplicateCandidateId;

  return (
    <>
      <Alert
        severity="warning"
        sx={{
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          "& .MuiAlert-action": {
            ml: { xs: 0, sm: "auto" },
            mt: { xs: 1, sm: 0 },
            pl: 0,
          },
        }}
        action={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            {duplicateCandidates.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-start" },
                  alignItems: "center",
                  order: { xs: 3, sm: 1 },
                }}
              >
                <Typography variant="caption" sx={{ mr: 2 }}>
                  {currentIndex + 1} of {duplicateCandidates.length}
                </Typography>
                {currentIndex > 0 && (
                  <Button
                    size="small"
                    onClick={handlePrevious}
                    disabled={isResolving}
                    sx={{ minWidth: "auto", mr: 1 }}
                  >
                    ←
                  </Button>
                )}
                {currentIndex < duplicateCandidates.length - 1 && (
                  <Button
                    size="small"
                    onClick={handleNext}
                    disabled={isResolving}
                    sx={{ minWidth: "auto" }}
                  >
                    →
                  </Button>
                )}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexDirection: { xs: "column", sm: "row" },
                order: { xs: 1, sm: 2 },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={isResolving}
                onClick={() =>
                  openConfirmDialog(DuplicateResolutionType.Ignore)
                }
                startIcon={<DoNotDisturbIcon />}
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                }}
              >
                Keep Separate
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={isResolving}
                onClick={() => openConfirmDialog(DuplicateResolutionType.Merge)}
                startIcon={
                  isResolving ? (
                    <CircularProgress size={16} />
                  ) : (
                    <MergeTypeIcon />
                  )
                }
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                }}
              >
                Merge
              </Button>
            </Box>
          </Box>
        }
      >
        <AlertTitle>Possible Duplicate Found</AlertTitle>
        <Box
          sx={{
            wordBreak: "break-word",
            fontSize: { xs: "0.875rem", sm: "1rem" },
          }}
        >
          <strong>{currentCandidate.sourceEntityName}</strong> vs{" "}
          <strong>{currentCandidate.duplicateEntityName}</strong>
          {currentCandidate.sourceEntityType &&
            ` (${currentCandidate.sourceEntityType})`}
        </Box>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: "block",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
            color: "text.secondary",
            fontStyle: "italic",
          }}
        >
          ℹ️ These lists update automatically once a day.
        </Typography>
      </Alert>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          {resolutionAction?.type === DuplicateResolutionType.Merge
            ? "Choose Which Record to Keep"
            : "Confirm - Keep Records Separate"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {resolutionAction?.type === DuplicateResolutionType.Merge ? (
            <>
              <Typography
                variant="body1"
                gutterBottom
                textAlign="center"
                color="text.secondary"
              >
                Select which record should be the primary one. The other
                record's data will be merged into it.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  component="legend"
                  sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}
                >
                  Choose the record to keep:
                </FormLabel>
                <RadioGroup
                  value={selectedEntityToKeep}
                  onChange={(e) =>
                    setSelectedEntityToKeep(
                      e.target.value as "source" | "duplicate"
                    )
                  }
                  sx={{ gap: 2 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      border: "2px solid",
                      borderColor:
                        selectedEntityToKeep === "source"
                          ? "primary.main"
                          : "divider",
                      borderRadius: 2,
                      backgroundColor:
                        selectedEntityToKeep === "source"
                          ? "primary.lighter"
                          : "background.paper",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "primary.light",
                        backgroundColor: "primary.lighter",
                      },
                    }}
                  >
                    <FormControlLabel
                      value="source"
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="primary.dark"
                          >
                            Record A: {currentCandidate.sourceEntityName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Type: {currentCandidate.sourceEntityType}
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      border: "2px solid",
                      borderColor:
                        selectedEntityToKeep === "duplicate"
                          ? "secondary.main"
                          : "divider",
                      borderRadius: 2,
                      backgroundColor:
                        selectedEntityToKeep === "duplicate"
                          ? "secondary.lighter"
                          : "background.paper",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "secondary.light",
                        backgroundColor: "secondary.lighter",
                      },
                    }}
                  >
                    <FormControlLabel
                      value="duplicate"
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Typography
                            variant="h6"
                            fontWeight="bold"
                            color="secondary.dark"
                          >
                            Record B: {currentCandidate.duplicateEntityName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Duplicate candidate
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start", m: 0 }}
                    />
                  </Box>
                </RadioGroup>
              </FormControl>
            </>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body1" gutterBottom>
                Confirm that these are different records that should remain
                separate in the system.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will mark them as "not duplicates" and they won't appear in
                future duplicate checks.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: "center", gap: 2 }}>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant="outlined"
            color="inherit"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmResolve}
            color={
              resolutionAction?.type === DuplicateResolutionType.Merge
                ? "success"
                : "error"
            }
            variant="contained"
            size="large"
            disabled={
              resolutionAction?.type === DuplicateResolutionType.Merge &&
              !selectedEntityToKeep
            }
            sx={{
              minWidth: 120,
              fontWeight: "600",
            }}
          >
            {resolutionAction?.type === DuplicateResolutionType.Merge
              ? "Merge Records"
              : "Keep Separate"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DuplicateCandidateAlert;
